import { keepPreviousData } from "@tanstack/react-query"
import { useDebounce as useDebouncedValue } from "hooks/use-debounced-value"
import { useSearchBroadcasts } from "sdk/command-palette/queries"
import { SUBPAGES } from "../pages"

export const PAGE_ID = "broadcast-search"

export const SHORTCUT = "f b"

// Don't fire a request on the first keystroke — wait for something searchable.
// Single-sourced with the subpage's "keep typing" hint (see pages.js).
const MIN_QUERY_LENGTH = SUBPAGES[PAGE_ID].minQueryLength

const DEBOUNCE_MS = 300

const SEARCH_ENTRY_COMMAND = {
  id: "search-broadcasts",
  label: "Search Broadcasts…",
  icon: "search",
  group: "Search",
  keywords: ["find", "broadcast", "email", "post", "campaign"],
  shortcut: SHORTCUT,
  onSelect: api => api.pushPage(PAGE_ID)
}

/**
 * Provider for broadcast search. At the root it offers the "Search broadcasts…"
 * entry that opens the subpage; on that subpage it returns the async,
 * server-filtered results (selecting one navigates to the broadcast). Reuses
 * the campaigns index search, so it's server-filtered like subscriber search
 * rather than preloaded like tags/segments.
 *
 * @type {import("../types").Provider}
 */
export function useBroadcastSearchProvider({ query, activePageId }) {
  const active = activePageId === PAGE_ID
  const trimmedQuery = query.trim()
  const debouncedQuery = useDebouncedValue(trimmedQuery, DEBOUNCE_MS)

  const { data, isFetching } = useSearchBroadcasts({
    params: { query: debouncedQuery },
    enabled:
      active &&
      debouncedQuery === trimmedQuery &&
      debouncedQuery.length >= MIN_QUERY_LENGTH,
    // Keep the previous results on screen while the next query loads, so the
    // list doesn't flash empty between debounced fetches.
    placeholderData: keepPreviousData
  })

  if (!activePageId) {
    return { commands: [SEARCH_ENTRY_COMMAND], isLoading: false }
  }

  if (!active) return { commands: [], isLoading: false }

  const commands = (data?.broadcasts ?? []).map(broadcast => ({
    id: `broadcast-${broadcast.id}`,
    label: broadcast.subject,
    icon: "megaphone",
    group: "Broadcasts",
    onSelect: api => api.navigate(broadcast.url)
  }))

  return { commands, isLoading: isFetching }
}

/**
 * @returns {import("../types").Shortcut[]}
 */
export function getBroadcastSearchShortcuts() {
  return [{ shortcut: SHORTCUT, onTrigger: api => api.pushPage(PAGE_ID) }]
}
